import React, { useState, useEffect, useRef } from 'react'
import { CButton } from '@coreui/react'
import MUIDataTable from 'mui-datatables'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ShimmerTable } from 'react-shimmer-effects'
import Modal from 'react-modal'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { AiOutlineAppstoreAdd } from 'react-icons/ai'
import { MdDeleteOutline } from 'react-icons/md'
import { CiEdit } from 'react-icons/ci'

import UserNameFilter from './Filters/UserNameFilter'
import ActivityFilter from './Filters/ActivityFilter'
import CreatedAtFilter from './Filters/CreatedAtFilter'

const UsersActivity = () => {
  const navigate = useNavigate()

  // State Management
  const [users, setUsers] = useState([])
  const [onLoading, setOnLoading] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [modalIsOpen, setModalIsOpen] = useState(false)

  // Pagination, Search and Filter states
  const [page, setPage] = useState(0)
  const [count, setCount] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchText, setSearchText] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [filterList, setFilterList] = useState({})
  const [filterOptions, setFilterOptions] = useState({
    roles: [],
    statuses: [],
    locations: [],
    businessUnits: [],
  })

  const prevSearchInput = useRef(searchInput)

  const fetchUsers = async (isFirstTime, page, rowsPerPage, searchText, filterList) => {
    if (isFirstTime) {
      setOnLoading(true)
    }

    const token = sessionStorage.getItem('ASIToken')
    try {
      const queryParams = new URLSearchParams()

      if (searchText) {
        queryParams.append('search', searchText)
      }

      if (Object.keys(filterList).length > 0) {
        queryParams.append('filterList', JSON.stringify(filterList))
      }

      const response = await axios.get(
        `/api/v1/organizations/users/activity/${page}/${rowsPerPage}${
          queryParams.toString() ? `?${queryParams.toString()}` : ''
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      setUsers(response.data.users)
      setCount(response.data.totalCount)
      setFilterOptions(response.data.filterOptions)
    } catch (error) {
     console.error('Error fetching users:', error);

    if (error.response && error.response.status === 403) {
      window.location.href = '/dashboard';
      // or navigate('/dashboard', { replace: true }); if using useNavigate
    } else {
      toast.error('Error loading users');
    }
  } finally {
    setOnLoading(false);
  }
  }

  useEffect(() => {
    fetchUsers(true, 0, rowsPerPage, '', {})
  }, [])

  useEffect(() => {
    if (prevSearchInput.current && searchInput.length === 0) {
      fetchUsers(true, 0, rowsPerPage, '', filterList)
    }
    prevSearchInput.current = searchInput
  }, [searchInput])

  const columns = [
    {
      name: 'index',
      label: '#',
      options: {
        filter: false,
        download: true,
        customBodyRender: (value, tableMeta) => {
          return page * rowsPerPage + tableMeta.rowIndex + 1
        },
      },
    },
    // {
    //   name: 'user',
    //   label: 'User Name',
    //   options: {
    //     filter: false,
    //     download: true,
    //     customBodyRender: (value) => {
    //       return value?.firstName || '-'
    //     },
    //   },
    // },
    {
      name: 'user',
      label: 'User Name',
      options: {
        filter: true,
        download: true,
        // sort: true,
        customBodyRender: (value) => {
          return value?.firstName || '-'
        },
        filterType: 'custom',
        filterList: filterList['User Name'] || [],

        filterOptions: {
          names: filterOptions?.userNameList || [],
          logic: (value, selected) => {
            if (!selected || selected.length === 0) return false
            return !selected.includes(value)
          },
          display: (filterList, onChange, index, column) => (
            <UserNameFilter
              filterList={filterList[index] || []}
              onChange={onChange}
              index={index}
              column={column}
              filterOptions={filterOptions}
            />
          ),
        },
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
      },
    },
    // {
    //   name: 'activityName',
    //   label: 'Activity',
    //   options: {
    //     filter: false,
    //     download: true,
    //   },
    // },
    {
      name: 'activityName',
      label: 'Activity',
      options: {
        filter: true,
        download: true,
        // sort: true,
        filterType: 'custom',
        filterList: filterList['Activity'] || [],

        filterOptions: {
          names: filterOptions?.activityNameList || [],
          logic: (value, selected) => {
            if (!selected || selected.length === 0) return false
            return !selected.includes(value)
          },
          display: (filterList, onChange, index, column) => (
            <ActivityFilter
              filterList={filterList[index] || []}
              onChange={onChange}
              index={index}
              column={column}
              filterOptions={filterOptions}
            />
          ),
        },
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
      },
    },
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        filter: true,
        download: true,
        // sort: true,
        filterType: 'custom',
        filterList: filterList['Created At'] || [],

        filterOptions: {
          names: filterOptions?.userActivityCreateAt || [],
          logic: (value, selected) => {
            if (!selected || selected.length === 0) return false
            return !selected.includes(value)
          },
          display: (filterList, onChange, index, column) => (
            <CreatedAtFilter
              filterList={filterList[index] || []}
              onChange={onChange}
              index={index}
              column={column}
              filterOptions={filterOptions}
            />
          ),
        },
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
      },
    },
    // {
    //   name: 'createdAt',
    //   label: 'Created At',
    //   options: {
    //     sort: true,
    //     sortDirection: 'desc',
    //     filter: false,
    //     customBodyRender: (value) => {
    //       const date = new Date(value)
    //       return date.toLocaleString('en-GB', {
    //         day: '2-digit',
    //         month: '2-digit',
    //         year: 'numeric',
    //         hour: '2-digit',
    //         minute: '2-digit',
    //         second: '2-digit',
    //       })
    //     },
    //   },
    // },
  ]

  const getMuiTheme = () =>
    createTheme({
      components: {
        MUIDataTableBodyCell: {
          styleOverrides: {
            root: {
              textAlign: 'left',
              '&:nth-child(1)': {
                width: 30,
              },
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              textAlign: 'left',
              '&:nth-child(1)': {
                width: 30,
              },
            },
          },
        },
      },
    })

  const options = {
    filterType: 'multiselect',
    responsive: 'stacked',
    elevation: 0,
    filter: true,
    download: true,
    print: false,
    downloadOptions: {
      filename: 'Users.csv',
      separator: ',',
    },
    search: true,
    searchOpen: true,
    viewColumns: true,
    selectableRows: false,
    serverSide: true,
    rowsPerPage: rowsPerPage,
    rowsPerPageOptions: [10, 20, 60, 100, 150],
    count: count,
    page: page,
    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
      customCSVdata: (displayData, selectedRows, displayedColumns) => {
        return displayData.map((row) => ({
          ...row,
          businessUnit: row.businessUnit?.name || '---',
        }))
      },
    },
    customSearchRender: (searchText, handleSearch, hideSearch, options) => {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 16px',
          }}
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setSearchText(searchInput)
                setPage(0)
                fetchUsers(true, 0, rowsPerPage, searchInput, filterList)
              }
            }}
            placeholder="Search users..."
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              width: '300px',
              fontSize: '14px',
              color: '#000',
            }}
          />
          <button
            onClick={() => {
              setSearchText(searchInput)
              setPage(0)
              fetchUsers(true, 0, rowsPerPage, searchInput, filterList)
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Search
          </button>
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('')
                setSearchText('')
                setPage(0)
                fetchUsers(true, 0, rowsPerPage, '', filterList)
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: '#d32f2f',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Clear
            </button>
          )}
        </div>
      )
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          setPage(tableState.page)
          fetchUsers(true, tableState.page, tableState.rowsPerPage, searchText, filterList)
          break

        case 'changeRowsPerPage':
          setRowsPerPage(tableState.rowsPerPage)
          setPage(0)
          fetchUsers(true, 0, tableState.rowsPerPage, searchText, filterList)
          break

        case 'filterChange':
          const newFilterList = {}
          tableState.filterList.forEach((filter, index) => {
            if (filter && filter.length) {
              newFilterList[columns[index].label] = filter
            }
          })
          setFilterList(newFilterList)
          setPage(0)
          fetchUsers(true, 0, tableState.rowsPerPage, searchText, newFilterList)
          break

        case 'resetFilters':
          setFilterList({})
          setPage(0)
          fetchUsers(true, 0, tableState.rowsPerPage, searchText, {})
          break
      }
    },
    textLabels: {
      body: {
        noMatch: onLoading ? 'Loading users...' : 'No users found',
      },
    },
  }

  const tableData = users

  return (
    <div className="activeScans">
      <div style={{ width: '100%' }}>
        <div>
          <div
            style={{
              marginBottom: '2rem',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <h4>
              <strong>Users Activity</strong>
            </h4>
          </div>

          {onLoading ? (
            <ShimmerTable row={8} col={10} />
          ) : (
            <ThemeProvider theme={getMuiTheme()}>
              <MUIDataTable
                style={{ height: '57vh' }}
                data={tableData}
                columns={columns}
                options={options}
              />
            </ThemeProvider>
          )}
        </div>
      </div>
    </div>
  )
}

export default UsersActivity
